import React, { useState } from 'react';
import { trackEvent } from '../lib/tracking';

export default function ContactForm({ subjectDefault = 'Generel henvendelse' }) {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    subject: subjectDefault,
    website_url: '', // Honeypot
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  function handleChange(e) {
    const { name, value } = e.target;
    if (status === 'idle' && (name === 'name' || name === 'email')) {
      trackEvent('contact_form_start');
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Der opstod en fejl ved afsendelse. Prøv venligst igen.');
      }

      setStatus('success');
      trackEvent('contact_form_submit', { subject: formData.subject });
      setFormData({
        name: '',
        phone: '',
        email: '',
        message: '',
        subject: subjectDefault,
        website_url: '',
      });
    } catch (err) {
      console.error(err);
      setStatus('error');
      setErrorMessage(err.message || 'Der opstod en uventet fejl. Du er også velkommen til at ringe på 26 81 26 81.');
    }
  }

  if (status === 'success') {
    return (
      <div className="bg-[#ECEEF1] p-8 sm:p-10 rounded-2xl border border-emerald-300 text-center animate-in fade-in duration-300">
        <div className="w-14 h-14 bg-emerald-100 text-emerald-800 rounded-full flex items-center justify-center mx-auto mb-4">
          <i className="fa-solid fa-circle-check text-2xl" aria-hidden="true" />
        </div>
        <h3 className="font-display text-2xl text-[#5A232E] mb-2">Tak for din henvendelse</h3>
        <p className="text-sm text-[#6c7480] max-w-md mx-auto leading-relaxed">
          Jeg har modtaget din besked og vender tilbage hurtigst muligt – typisk inden for 24 timer på hverdage.
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-6 px-5 py-2.5 rounded-xl bg-[#5A232E] text-white text-xs font-medium hover:bg-[#431722] transition-colors"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 text-left">
      {/* Honeypot field - strictly hidden with sr-only and absolute positioning */}
      <div className="sr-only absolute" aria-hidden="true" style={{ opacity: 0, position: 'absolute', top: 0, left: 0, height: 0, width: 0, zIndex: -1 }}>
        <label htmlFor="website_url">Lad dette felt stå tomt</label>
        <input
          type="text"
          id="website_url"
          name="website_url"
          value={formData.website_url}
          onChange={handleChange}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-[#5A232E] mb-1.5">
            Navn <span className="text-[#5A232E]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            required
            value={formData.name}
            onChange={handleChange}
            placeholder="Dit fulde navn"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#D9DDE2] focus:outline-hidden focus:ring-2 focus:ring-[#5A232E] text-sm text-[#11161d] transition-all"
          />
        </div>

        <div>
          <label htmlFor="phone" className="block text-xs font-semibold uppercase tracking-wider text-[#5A232E] mb-1.5">
            Telefonnummer <span className="text-[#5A232E]">*</span>
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            required
            value={formData.phone}
            onChange={handleChange}
            placeholder="Fx 26 81 26 81"
            className="w-full px-4 py-3 rounded-xl bg-white border border-[#D9DDE2] focus:outline-hidden focus:ring-2 focus:ring-[#5A232E] text-sm text-[#11161d] transition-all"
          />
        </div>
      </div>

      <div>
        <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-[#5A232E] mb-1.5">
          E-mailadresse <span className="text-[#5A232E]">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={formData.email}
          onChange={handleChange}
          placeholder="din@email.dk"
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#D9DDE2] focus:outline-hidden focus:ring-2 focus:ring-[#5A232E] text-sm text-[#11161d] transition-all"
        />
      </div>

      <div>
        <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-[#5A232E] mb-1.5">
          Hvad vil du gerne tale med Kim om? <span className="text-[#5A232E]">*</span>
        </label>
        <textarea
          id="message"
          name="message"
          required
          rows={4}
          value={formData.message}
          onChange={handleChange}
          placeholder="Kort beskrivelse af din udfordring eller dit ønske for en samtale..."
          className="w-full px-4 py-3 rounded-xl bg-white border border-[#D9DDE2] focus:outline-hidden focus:ring-2 focus:ring-[#5A232E] text-sm text-[#11161d] transition-all resize-y"
        />
        <p className="text-[11px] text-[#6c7480] mt-1">
          Undgå at indsende unødvendigt detaljerede følsomme sundhedsoplysninger. Vi uddyber sammen under samtalen.
        </p>
      </div>

      {status === 'error' && (
        <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-xs text-rose-800 flex items-start gap-2">
          <i className="fa-solid fa-triangle-exclamation mt-0.5 shrink-0" aria-hidden="true" />
          <span>{errorMessage}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full sm:w-auto px-8 py-3.5 rounded-xl bg-[#5A232E] hover:bg-[#431722] text-white font-semibold text-sm shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
      >
        {status === 'loading' ? (
          <>
            <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
            <span>Sender besked...</span>
          </>
        ) : (
          <>
            <i className="fa-solid fa-paper-plane" aria-hidden="true" />
            <span>Send besked</span>
          </>
        )}
      </button>
    </form>
  );
}
